import { html, css } from "lit";
import { SignalWatcher } from "@lit-labs/preact-signals";
import { ifDefined } from "lit-html/directives/if-defined.js";
import { when } from "lit-html/directives/when.js";
import { query, state } from "lit/decorators.js";
import "../Link/index.js";
import "../Badge/index.js";
import "../../atom/Column/index.js";
import "../../atom/Row/index.js";
import "../../atom/Text/index.js";
import { BaseComponent } from "../../BaseComponent.js";
import { defineElement } from "../../../decorators/defineElement.js";
import { prop } from "../../../decorators/prop/index.js";
import { isMobile } from "../../../responsive/index.js";
import { fileSizeInMbFormatted, getMessageClasses, timestampFormatted, getWrapperClasses } from "./utils.js";
var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result) __defProp(target, key, result);
  return result;
};
let DsfFileManager = class extends SignalWatcher(BaseComponent) {
  constructor() {
    super(...arguments);
    this.dsf_file_size = 0;
    this.dsf_locale = navigator.language;
    this.dsf_error = false;
    this.dsf_disable_download = false;
    this._hasActionsSlotContent = false;
  }
  handleLinkClick() {
    this.emit("file-click");
  }
  handleActionsSlotChange() {
    this._hasActionsSlotContent = !!this.actionsSlotElement && this.actionsSlotElement.assignedElements({ flatten: true }).length > 0;
  }
  renderFileInfoComponent() {
    return html`
      <div class="flex flex-col gap-4">
        <div class="flex items-baseline gap-8">
          <dsf-link
            class="dsf-file-link shrink truncate"
            dsf_size="medium"
            ?disabled=${this.dsf_disable_download || this.dsf_error}
            dsf_href=${ifDefined(this.dsf_file_url)}
            title="${ifDefined(this.dsf_file_name)}"
            @click=${this.handleLinkClick}
          >
            ${this.dsf_file_name}
          </dsf-link>
          ${when(
      this.dsf_file_size,
      () => html`<span class="dsf-text-xxs text-neutral-300"
              >${fileSizeInMbFormatted(this.dsf_file_size)}</span
            >`
    )}
        </div>
        ${when(
      this.dsf_message,
      () => html`<div class="flex items-center gap-4 text-nowrap">
            <span class=${getMessageClasses(this)}>${this.dsf_message}</span>
            <span class="dsf-text-xxs text-neutral-300"
              >${timestampFormatted(this.dsf_timestamp, this.dsf_locale)}</span
            >
          </div>`
    )}
      </div>
    `;
  }
  renderBadgeComponent() {
    let badgeLevel = "info";
    if (this.dsf_status === "approved") {
      badgeLevel = "success";
    } else if (this.dsf_status === "rejected") {
      badgeLevel = "critical";
    }
    return when(
      this.dsf_status,
      () => html`
        <dsf-badge dsf_level=${badgeLevel} class="whitespace-nowrap">
          ${this.dsf_status_label}
        </dsf-badge>
      `
    );
  }
  renderTextComponent() {
    const desktopClasses = "flex flex-auto justify-center";
    const mobileClasses = "flex flex-auto";
    return html`
      ${when(
      this.dsf_text_title || this.dsf_text_message,
      () => html`
          <div class=${isMobile() ? mobileClasses : desktopClasses}>
            <div class="flex flex-col">
              ${when(
        this.dsf_text_title,
        () => html`
                  <dsf-text dsf_level=${isMobile() ? "xs-semibold" : "xxs-semibold"}
                    >${this.dsf_text_title}</dsf-text
                  >
                `
      )}
              ${when(
        this.dsf_text_message,
        () => html`
                  <dsf-text dsf_level=${isMobile() ? "xs" : "xxs"}
                    >${this.dsf_text_message}</dsf-text
                  >
                `
      )}
            </div>
          </div>
        `
    )}
    `;
  }
  renderButtonSlot() {
    return html`<slot name="buttons" class="flex items-center gap-4"></slot>`;
  }
  renderMobile() {
    const wrapperClasses = getWrapperClasses(this, true);
    const actionsClasses = `flex ${!this._hasActionsSlotContent ? "hidden" : ""}`;
    return html`<div class=${wrapperClasses}>
      <dsf-row dsf_gap="xxl">
        ${this.renderFileInfoComponent()} ${this.renderBadgeComponent()}
        <div class="ml-auto">${this.renderButtonSlot()}</div>
      </dsf-row>
      ${this.renderTextComponent()}
      <slot
        name="actions"
        class=${actionsClasses}
        @slotchange=${this.handleActionsSlotChange}
      ></slot>
    </div>`;
  }
  renderDesktop() {
    const wrapperClasses = getWrapperClasses(this, false);
    return html`<div class=${wrapperClasses}>
      ${this.renderFileInfoComponent()} ${this.renderBadgeComponent()} ${this.renderTextComponent()}
      <div class="ml-auto flex gap-32">
        <slot name="actions"></slot>
        ${this.renderButtonSlot()}
      </div>
    </div>`;
  }
  render() {
    if (isMobile()) {
      return this.renderMobile();
    }
    return this.renderDesktop();
  }
};
DsfFileManager.styles = [
  ...BaseComponent.styles,
  css`
      .dsf-file-link::part(link) {
        display: inline;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        min-width: 0;
      }
    `
];
__decorateClass([
  prop({ type: String })
], DsfFileManager.prototype, "dsf_file_name", 2);
__decorateClass([
  prop({ type: String })
], DsfFileManager.prototype, "dsf_file_url", 2);
__decorateClass([
  prop({ type: Number })
], DsfFileManager.prototype, "dsf_file_size", 2);
__decorateClass([
  prop({ type: String })
], DsfFileManager.prototype, "dsf_message", 2);
__decorateClass([
  prop({ type: String })
], DsfFileManager.prototype, "dsf_timestamp", 2);
__decorateClass([
  prop({ type: String })
], DsfFileManager.prototype, "dsf_locale", 2);
__decorateClass([
  prop({ type: Boolean, reflect: true })
], DsfFileManager.prototype, "dsf_error", 2);
__decorateClass([
  prop({ type: Boolean, reflect: true })
], DsfFileManager.prototype, "dsf_disable_download", 2);
__decorateClass([
  prop({ type: String, reflect: true })
], DsfFileManager.prototype, "dsf_status", 2);
__decorateClass([
  prop({ type: String })
], DsfFileManager.prototype, "dsf_status_label", 2);
__decorateClass([
  prop({ type: String })
], DsfFileManager.prototype, "dsf_text_title", 2);
__decorateClass([
  prop({ type: String })
], DsfFileManager.prototype, "dsf_text_message", 2);
__decorateClass([
  query('slot[name="actions"]')
], DsfFileManager.prototype, "actionsSlotElement", 2);
__decorateClass([
  state()
], DsfFileManager.prototype, "_hasActionsSlotContent", 2);
DsfFileManager = __decorateClass([
  defineElement("dsf-file-manager")
], DsfFileManager);
export {
  DsfFileManager
};
